import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, CheckCircle, Database, Brain, Shield, Globe, BarChart3, ArrowLeft,
  ArrowUpRight, Code2, Circle, X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOMAINS = [
  'Data Structures & Algorithms',
  'AI / Machine Learning',
  'Blockchain',
  'Cloud Computing',
  'Cybersecurity',
  'Data Science',
];

const DOMAIN_ICONS = {
  'AI / Machine Learning': Brain,
  'Data Structures & Algorithms': Database,
  'Blockchain': Shield,
  'Cloud Computing': Globe,
  'Cybersecurity': Shield,
  'Data Science': BarChart3,
};

const DIFF_PILLS = {
  easy:   { label: 'Easy',   glyph: '01', bg: 'bg-[#E8F5EC]', fg: 'text-[#0F7A3A]', ring: 'ring-[#0F7A3A]/20' },
  medium: { label: 'Medium', glyph: '02', bg: 'bg-[#FFF4DE]', fg: 'text-[#9A5C00]', ring: 'ring-[#9A5C00]/25' },
  hard:   { label: 'Hard',   glyph: '03', bg: 'bg-[#FDE4E1]', fg: 'text-[#B2311A]', ring: 'ring-[#B2311A]/25' },
};

const PAGE_SIZE = 20;

const CodeArenaProblemsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProblems, setTotalProblems] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    difficulty: searchParams.get('difficulty') || '',
    domain: searchParams.get('domain') || '',
    search: '',
  });

  useEffect(() => {
    const fetchProblems = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', PAGE_SIZE);
        if (filters.difficulty) params.append('difficulty', filters.difficulty);
        if (filters.domain) params.append('domain', filters.domain);
        const res = await axios.get(`${API}/codearena/problems?${params.toString()}`);
        setProblems(res.data.problems || []);
        setTotalProblems(res.data.total || 0);
        setTotalPages(res.data.total_pages || 1);
      } catch (err) {
        console.error('Failed to fetch problems:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProblems();
  }, [page, filters.difficulty, filters.domain]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    setSearchParams(newParams);
  };

  const filteredProblems = useMemo(() => {
    if (!filters.search) return problems;
    const needle = filters.search.toLowerCase();
    return problems.filter(p =>
      p.title?.toLowerCase().includes(needle) ||
      p.description?.toLowerCase().includes(needle) ||
      p.tags?.some(t => t.toLowerCase().includes(needle))
    );
  }, [problems, filters.search]);

  const activeChips = [
    filters.difficulty && { key: 'difficulty', label: DIFF_PILLS[filters.difficulty]?.label || filters.difficulty },
    filters.domain && { key: 'domain', label: filters.domain },
    filters.search && { key: 'search', label: `"${filters.search}"` },
  ].filter(Boolean);

  const clearAll = () => {
    setFilters({ difficulty: '', domain: '', search: '' });
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]" style={{ fontFamily: "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif" }} data-testid="codearena-problems-page">
      {/* ---- Masthead ---- */}
      <header className="border-b border-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-6 pb-2">
          <Link to="/codearena" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#6B6B6B] hover:text-[#0A0A0A] transition-colors" data-testid="back-to-codearena">
            <ArrowLeft size={12} />
            EvalentX Arena
          </Link>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-8 pt-3 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#6B6B6B] mb-3">§01 · Problem Set</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[0.95] tracking-[-0.02em]">
              Solve.<br />
              Verify. <span className="text-[#6B6B6B]">Stand out.</span>
            </h1>
          </div>
          <div className="lg:col-span-5 lg:pl-8 lg:border-l border-[#0A0A0A]">
            <div className="grid grid-cols-3 gap-6">
              <Metric num={totalProblems} label="Problems" />
              <Metric num={DOMAINS.length} label="Domains" />
              <Metric num="3" label="Difficulties" />
            </div>
            <p className="text-[13px] text-[#3C3C3C] mt-6 leading-relaxed max-w-sm">
              Curated coding problems across six frontier domains. Every solved problem counts toward your EvalentX talent score and verified rank.
            </p>
          </div>
        </div>
      </header>

      {/* ---- Controls bar ---- */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-[#E5E5E5]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9A9A]" size={15} />
            <input
              type="text"
              placeholder="Search problems, tags, topics…"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-9 pr-3 py-2 bg-transparent border border-[#0A0A0A] text-[13px] placeholder-[#9A9A9A] focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/10"
              data-testid="search-input"
            />
          </div>

          {/* Difficulty segmented control */}
          <div className="flex border border-[#0A0A0A] text-[11px] uppercase tracking-[0.18em] font-semibold">
            {['', 'easy', 'medium', 'hard'].map((diff, i) => (
              <button
                key={diff || 'all'}
                onClick={() => handleFilterChange('difficulty', diff)}
                className={`px-3.5 py-2 transition-colors ${i > 0 ? 'border-l border-[#0A0A0A]' : ''} ${
                  filters.difficulty === diff
                    ? 'bg-[#0A0A0A] text-white'
                    : 'bg-white text-[#0A0A0A] hover:bg-[#0A0A0A]/5'
                }`}
                data-testid={`filter-difficulty-${diff || 'all'}`}
              >
                {diff || 'All'}
              </button>
            ))}
          </div>

          {activeChips.length > 0 && (
            <button
              onClick={clearAll}
              className="text-[11px] uppercase tracking-[0.2em] text-[#6B6B6B] hover:text-[#0A0A0A] transition-colors ml-auto"
              data-testid="clear-filters"
            >
              Clear all ×
            </button>
          )}
        </div>

        {activeChips.length > 0 && (
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-3 flex items-center gap-2 flex-wrap">
            {activeChips.map(c => (
              <button
                key={c.key}
                onClick={() => handleFilterChange(c.key, '')}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#0A0A0A] text-[11px] uppercase tracking-[0.14em] hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                {c.label}
                <X size={11} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ---- Main body ---- */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Sidebar — Domain filter (Swiss index style) */}
        <aside className="lg:col-span-3">
          <div className="sticky top-28">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#6B6B6B] mb-4 pb-3 border-b border-[#0A0A0A]">§02 · Domain</p>
            <ul className="divide-y divide-[#E5E5E5]" data-testid="domain-filter-list">
              <DomainRow index="00" name="All Domains" active={filters.domain === ''} onClick={() => handleFilterChange('domain', '')} testId="filter-domain-all" />
              {DOMAINS.map((domain, idx) => {
                const Icon = DOMAIN_ICONS[domain] || Code2;
                return (
                  <DomainRow
                    key={domain}
                    index={String(idx + 1).padStart(2, '0')}
                    name={domain}
                    icon={Icon}
                    active={filters.domain === domain}
                    onClick={() => handleFilterChange('domain', domain)}
                    testId={`filter-domain-${domain.replace(/\s+/g, '-').toLowerCase()}`}
                  />
                );
              })}
            </ul>
          </div>
        </aside>

        {/* Problem list */}
        <section className="lg:col-span-9">
          <div className="flex items-baseline justify-between mb-6 pb-3 border-b border-[#0A0A0A]">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#6B6B6B]">§03 · The List</p>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#6B6B6B]">
              <span className="text-[#0A0A0A] font-semibold">{filteredProblems.length}</span>
              {filters.search ? ' matches' : ' showing'}
              {totalPages > 1 && (
                <span className="text-[#9A9A9A]"> · P{page}/{totalPages}</span>
              )}
            </p>
          </div>

          {loading ? (
            <ul className="divide-y divide-[#E5E5E5] border-t border-[#E5E5E5]">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <li key={i} className="py-6 animate-pulse">
                  <div className="h-3 bg-[#F0F0F0] w-20 mb-4" />
                  <div className="h-6 bg-[#F0F0F0] w-2/3 mb-3" />
                  <div className="h-3 bg-[#F0F0F0] w-1/2" />
                </li>
              ))}
            </ul>
          ) : filteredProblems.length > 0 ? (
            <>
              <ul className="divide-y divide-[#E5E5E5] border-t border-[#E5E5E5]" data-testid="problems-list">
                {filteredProblems.map((problem, idx) => (
                  <ProblemRow key={problem.id} problem={problem} index={(page - 1) * PAGE_SIZE + idx + 1} />
                ))}
              </ul>

              {totalPages > 1 && (
                <nav className="flex items-center justify-between gap-4 pt-8 mt-6 border-t border-[#0A0A0A]">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="text-[12px] uppercase tracking-[0.2em] font-semibold border-b border-[#0A0A0A] pb-0.5 hover:text-[#6B6B6B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="prev-page"
                  >
                    ← Previous
                  </button>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#6B6B6B]">
                    Page {String(page).padStart(2, '0')} / {String(totalPages).padStart(2, '0')}
                  </p>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="text-[12px] uppercase tracking-[0.2em] font-semibold border-b border-[#0A0A0A] pb-0.5 hover:text-[#6B6B6B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="next-page"
                  >
                    Next →
                  </button>
                </nav>
              )}
            </>
          ) : (
            <div className="border-t border-[#E5E5E5] py-20 text-center">
              <Circle size={28} className="mx-auto text-[#D4D4D4] mb-5" />
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#6B6B6B] mb-2">Nothing here</p>
              <p className="text-[15px] text-[#3C3C3C] max-w-sm mx-auto">
                No problems match your current filters. Try clearing one or two to widen the search.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

// ---- Small local components ----

const Metric = ({ num, label }) => (
  <div>
    <p className="text-4xl font-semibold tracking-[-0.02em] leading-none">{num}</p>
    <p className="text-[10px] uppercase tracking-[0.24em] text-[#6B6B6B] mt-2">{label}</p>
  </div>
);

const DomainRow = ({ index, name, icon: Icon, active, onClick, testId }) => (
  <li>
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-3 py-3 text-left transition-colors group ${
        active ? 'text-[#0A0A0A]' : 'text-[#6B6B6B] hover:text-[#0A0A0A]'
      }`}
    >
      <span className={`text-[10px] tabular-nums tracking-[0.12em] w-7 ${active ? 'text-[#0A0A0A] font-semibold' : 'text-[#9A9A9A]'}`}>{index}</span>
      {Icon && <Icon size={13} className={active ? 'text-[#0A0A0A]' : 'text-[#9A9A9A] group-hover:text-[#0A0A0A]'} strokeWidth={1.5} />}
      <span className={`text-[13px] flex-1 ${active ? 'font-semibold' : ''}`}>{name}</span>
      {active && <CheckCircle size={13} className="text-[#0A0A0A]" />}
    </button>
  </li>
);

const ProblemRow = ({ problem, index }) => {
  const diff = DIFF_PILLS[problem.difficulty?.toLowerCase()] || DIFF_PILLS.easy;
  const firstDomain = problem.domains?.[0];
  const DomainIcon = firstDomain ? (DOMAIN_ICONS[firstDomain] || Code2) : null;
  return (
    <li>
      <Link
        to={`/codearena/problem/${problem.id}`}
        data-testid={`problem-item-${index - 1}`}
        className="group grid grid-cols-[64px_1fr_auto] gap-5 lg:gap-8 py-7 items-start hover:bg-[#FAFAFA] -mx-6 lg:-mx-10 px-6 lg:px-10 transition-colors"
      >
        {/* Index */}
        <div className="pt-1">
          <p className="text-[11px] tabular-nums tracking-[0.18em] text-[#9A9A9A] font-semibold">
            {String(index).padStart(3, '0')}
          </p>
        </div>

        {/* Middle */}
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] font-semibold ring-1 ${diff.bg} ${diff.fg} ${diff.ring}`}>
              <span className="tabular-nums opacity-60">{diff.glyph}</span>
              {diff.label}
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#0A0A0A] font-semibold">
              +{problem.points} pts
            </span>
          </div>

          <h3 className="text-[22px] sm:text-[24px] font-semibold tracking-[-0.015em] leading-tight mb-2 group-hover:underline underline-offset-4 decoration-[#0A0A0A]">
            {problem.title}
          </h3>

          {problem.description && (
            <p className="text-[14px] text-[#3C3C3C] leading-relaxed line-clamp-2 max-w-2xl mb-3">
              {problem.description}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] uppercase tracking-[0.14em] text-[#6B6B6B]">
            {firstDomain && (
              <span className="inline-flex items-center gap-1.5">
                {DomainIcon && <DomainIcon size={11} strokeWidth={1.5} />}
                {firstDomain}
              </span>
            )}
            {problem.tags?.slice(0, 4).map((tag, tidx) => (
              <span key={tidx} className="text-[#9A9A9A]">· {tag}</span>
            ))}
          </div>
        </div>

        {/* Right arrow */}
        <div className="pt-2 self-start">
          <ArrowUpRight
            size={22}
            strokeWidth={1.5}
            className="text-[#0A0A0A] transition-transform duration-200 group-hover:-translate-y-1 group-hover:translate-x-1"
          />
        </div>
      </Link>
    </li>
  );
};

export default CodeArenaProblemsPage;
